import { ActiveElement, Chart, ChartType, Interaction, InteractionModeFunction, Plugin, Tooltip } from 'chart.js';

/**
 * The board's charts move together:
 *
 * - interaction mode 'boardX': per visible dataset the point nearest in time to the pointer (the series of
 *   one board chart come from different sources with their own timestamps, so 'index' would pair the
 *   wrong points); nothing for a dataset without a point near that time. A stepped series (a value that
 *   holds: valve, on / off) gives the value held at that time, not the next telegram.
 * - tooltip position 'boardTime': at the time shown (the pointer, or the synchronised line), and
 *   {@link tooltipTime} for its title — not the time of a held value's telegram.
 * - plugin 'boardSync' (options.plugins.boardSync.group = '<group name>'): hovering or tapping a time in
 *   one chart of a group draws a vertical line at that time in every chart of the group and shows their
 *   tooltips at the nearest points. Group members also keep vertical page scrolling on touch screens
 *   (the zoom plugin's pinch recogniser would block it): two fingers pinch, one finger scrolls.
 * - {@link alignAxis} (afterFit of every y axis of a board chart): the y axes of a group's charts take the
 *   widest width any chart of the group needs on that side, so every plot area spans the same x range and
 *   a time is at the same x in every chart. Without a group (the PDF's offscreen charts) the widths
 *   are fixed by options.plugins.boardSync.axes = { left, right }, or left as they are.
 */

declare module 'chart.js' {
  interface InteractionModeMap {
    boardX: InteractionModeFunction;
  }
  interface TooltipPositionerMap {
    boardTime: (items: readonly ActiveElement[], eventPosition: { x: number; y: number }) => { x: number; y: number } | false;
  }
  interface PluginOptionsByType<TType extends ChartType> {
    boardSync?: { group?: string | null; axes?: AxisWidths | null };
  }
}

/** The points nearest to time `ts` (one per visible dataset, if one is near enough). */
export function itemsAt(chart: Chart, ts: number): ActiveElement[] {
  const x: any = chart.scales['x'];
  if (!x || !isFinite(ts)) return [];
  const span = x.max - x.min;
  const out: ActiveElement[] = [];
  chart.data.datasets.forEach((_, i) => {
    if (!chart.isDatasetVisible(i)) return;
    const meta: any = chart.getDatasetMeta(i);
    const parsed: { x: number; y: number | null }[] = meta._parsed ?? [];
    const n = parsed.length;
    if (!n) return;
    let lo = 0, hi = n - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (parsed[mid].x < ts) lo = mid + 1; else hi = mid;
    }
    const stepped = (chart.data.datasets[i] as any).stepped;
    if (stepped === 'before' || stepped === true) {
      // a held value (valve, on / off): the last point at or before the time — not the next telegram
      const k = parsed[lo] && parsed[lo].x <= ts ? lo : lo - 1;
      const p = parsed[k];
      if (!p || p.y == null || !isFinite(p.y)) return;   // before the first value, or in a gap
      const el = meta.data?.[k];
      if (el) out.push({ element: el, datasetIndex: i, index: k });
      return;
    }
    // the nearer neighbour with a value (gaps are null)
    let best = -1, bestD = Infinity;
    for (const k of [lo - 1, lo, lo + 1]) {
      const p = parsed[k];
      if (!p || p.y == null || !isFinite(p.y)) continue;
      const d = Math.abs(p.x - ts);
      if (d < bestD) { bestD = d; best = k; }
    }
    if (best < 0) return;
    const step = n > 1 ? (parsed[n - 1].x - parsed[0].x) / (n - 1) : span;
    if (bestD > Math.max(1.5 * step, 0.02 * span)) return;
    const element = meta.data?.[best];
    if (element) out.push({ element, datasetIndex: i, index: best });
  });
  return out;
}

const boardX: InteractionModeFunction = (chart, e) => {
  const x: any = chart.scales['x'];
  const ca = chart.chartArea;
  if (!x || e.x == null || !ca || e.x < ca.left || e.x > ca.right) return [];
  return itemsAt(chart as Chart, x.getValueForPixel(e.x));
};

/** Widths (CSS px) of the left and right y axes. */
export interface AxisWidths { left: number; right: number; }

interface SyncGroup {
  charts: Set<Chart>;
  ts: number | null;
  /** What each chart's y axes need (their labels). */
  natural: Map<Chart, AxisWidths>;
  /** The widths the group's axes get: the largest natural ones. */
  axes: AxisWidths;
  relayout: boolean;
}
const groups = new Map<string, SyncGroup>();
const memberOf = new WeakMap<Chart, SyncGroup>();

function groupName(chart: Chart): string | null {
  const g = (chart.config.options as any)?.plugins?.boardSync?.group;
  return typeof g === 'string' && g ? g : null;
}

/** Show the time `ts` (null: none) in every chart of the group but `source` (which handles its own event). */
function showTime(g: SyncGroup, ts: number | null, source: Chart | null): void {
  if (g.ts === ts) return;
  g.ts = ts;
  for (const c of g.charts) {
    if (c === source || !c.chartArea) continue;
    const items = ts == null ? [] : itemsAt(c, ts);
    const px = ts == null ? 0 : (c.scales['x'] as any).getPixelForValue(ts);
    c.setActiveElements(items);
    c.tooltip?.setActiveElements(items, { x: px, y: (c.chartArea.top + c.chartArea.bottom) / 2 });
    c.draw();
  }
}

/** The widest natural axis widths of the group's charts. */
function widest(g: SyncGroup): AxisWidths {
  const w: AxisWidths = { left: 0, right: 0 };
  for (const n of g.natural.values()) { w.left = Math.max(w.left, n.left); w.right = Math.max(w.right, n.right); }
  return w;
}

/** The group's widths changed: lay out its charts again (once, on the next frame; all of them — the widths
 *  may change several times before it, each time from another chart). */
function relayout(g: SyncGroup): void {
  if (g.relayout) return;
  g.relayout = true;
  requestAnimationFrame(() => {
    g.relayout = false;
    for (const c of g.charts) if (c.ctx) c.update('none');
  });
}

/** Note a chart's natural axis widths; true if the group's widths changed. */
function noteNatural(g: SyncGroup, chart: Chart, side: 'left' | 'right', width: number): boolean {
  const n = g.natural.get(chart) ?? { left: 0, right: 0 };
  n[side] = width;
  g.natural.set(chart, n);
  const w = widest(g);
  if (w.left === g.axes.left && w.right === g.axes.right) return false;
  g.axes = w;
  return true;
}

/**
 * Scale option afterFit of every y axis of a board chart (left and right): the width the group's charts need
 * on that side (or the fixed width of options.plugins.boardSync.axes), never less than its own labels need.
 * Chart.js lays the chart out with the width set here.
 */
export function alignAxis(scale: any): void {
  const chart: Chart | undefined = scale?.chart;
  if (!chart) return;
  const side: 'left' | 'right' = scale.position === 'right' ? 'right' : 'left';
  const natural = scale.width;
  const fixed = (chart.config.options as any)?.plugins?.boardSync?.axes as AxisWidths | null | undefined;
  if (fixed) { scale.width = Math.max(natural, fixed[side] ?? 0); return; }
  const g = memberOf.get(chart);
  if (!g) return;
  if (noteNatural(g, chart, side, natural)) relayout(g);
  scale.width = Math.max(natural, g.axes[side]);
}

/** The natural y-axis widths of a laid-out chart (for fixed widths over several charts, e.g. the PDF). */
export function naturalAxes(chart: Chart): AxisWidths {
  const w: AxisWidths = { left: 0, right: 0 };
  for (const s of Object.values(chart.scales) as any[]) {
    if (s.isHorizontal()) continue;
    const side = s.position === 'right' ? 'right' : 'left';
    w[side] = Math.max(w[side], s.width);
  }
  return w;
}

/** Clear the crosshair of a group (e.g. when its window changes). */
export function clearBoardTime(group: string): void {
  const g = groups.get(group);
  if (g) showTime(g, null, null);
}

export const boardSyncPlugin: Plugin = {
  id: 'boardSync',

  afterInit(chart) {
    const name = groupName(chart);
    if (!name) return;
    let g = groups.get(name);
    if (!g) { g = { charts: new Set(), ts: null, natural: new Map(), axes: { left: 0, right: 0 }, relayout: false }; groups.set(name, g); }
    g.charts.add(chart);
    memberOf.set(chart, g);
  },

  afterUpdate(chart) {
    if (memberOf.has(chart) && chart.canvas) chart.canvas.style.touchAction = 'pan-y';
  },

  afterEvent(chart, args) {
    const g = memberOf.get(chart);
    if (!g) return;
    const e: any = args.event;
    if (e.type === 'mouseout') { showTime(g, null, chart); args.changed = true; return; }
    if (e.type !== 'mousemove' && e.type !== 'click' && e.type !== 'touchstart' && e.type !== 'touchmove') return;
    const ca = chart.chartArea;
    const inside = !!ca && e.x >= ca.left && e.x <= ca.right && e.y >= ca.top && e.y <= ca.bottom;
    const ts = inside ? (chart.scales['x'] as any).getValueForPixel(e.x) : null;
    if (ts === g.ts) return;
    showTime(g, ts, chart);
    args.changed = true;
  },

  // under the tooltip (drawn after the datasets)
  afterDatasetsDraw(chart) {
    const g = memberOf.get(chart);
    const x: any = chart.scales['x'];
    if (!g || g.ts == null || !x || g.ts < x.min || g.ts > x.max) return;
    const px = x.getPixelForValue(g.ts);
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(px, chartArea.top);
    ctx.lineTo(px, chartArea.bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(128,128,128,0.7)';
    ctx.setLineDash([4, 3]);
    ctx.stroke();
    ctx.restore();
  },

  afterDestroy(chart) {
    const g = memberOf.get(chart);
    if (!g) return;
    g.charts.delete(chart);
    memberOf.delete(chart);
    g.natural.delete(chart);
    if (!g.charts.size) { for (const [k, v] of groups) if (v === g) groups.delete(k); return; }
    // the widest chart may be gone: the others get narrower axes
    const w = widest(g);
    if (w.left !== g.axes.left || w.right !== g.axes.right) { g.axes = w; relayout(g); }
  },
};

let registered = false;
/** The time a board tooltip is for (where the pointer / the synchronised line is), else its first point's. */
export function tooltipTime(items: any[]): number | null {
  const chart: any = items[0]?.chart;
  const pos = chart?.tooltip?._eventPosition;
  const x = chart?.scales?.['x'];
  if (pos && x && isFinite(pos.x)) {
    const ts = x.getValueForPixel(pos.x);
    if (isFinite(ts)) return ts;
  }
  const p = items[0]?.parsed?.x;
  return typeof p === 'number' ? p : null;
}

/** Register the interaction mode, the tooltip position and the plugin (once). */
export function registerBoardSync(): void {
  if (registered) return;
  registered = true;
  (Interaction.modes as any).boardX = boardX;
  (Tooltip.positioners as any).boardTime = (items: readonly ActiveElement[], pos: { x: number; y: number } | null) =>
    items.length && pos && isFinite(pos.x) && isFinite(pos.y) ? { x: pos.x, y: pos.y } : false;
  Chart.register(boardSyncPlugin);
}
